import { Button, Input, Spin } from "antd";
import axios from "axios";
import debounce from "lodash/debounce";
import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { AiOutlineEye } from "react-icons/ai";
import { BiCopy, BiEdit } from "react-icons/bi";
import { FaPlus } from "react-icons/fa";
import { MdDeleteOutline } from "react-icons/md";
import { TfiDownload } from "react-icons/tfi";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { api, endpoint } from "@/api";
import { DataTable } from "@/components";
import appConfig from "@/config/appConfig";
import { userRoleFormate } from "@/constant";
import { pdf } from "@react-pdf/renderer";
import ReactPDF from "./ReactPDF";
import { flare } from "@flareapp/js";

const CustomReportList = () => {
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const { business, userDetails } = useSelector((state) => state.user);
  const firstBusiness = userDetails?.business?.[0] || {};

  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [limit, setLimit] = useState(Number(searchParams.get("limit")) || 10);
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
  const [total, setTotal] = useState(0);
  const [downloadPdf, setDownloadPdf] = useState([]);

  useEffect(() => {
    setLimit(Number(searchParams.get("limit")) || 10);
    setPage(Number(searchParams.get("page")) || 1);
  }, [searchParams]);

  const getReportList = debounce(async (query) => {
    try {
      setIsLoading(true);
      const response = await api.get(endpoint.get_customReport_list, {
        withCredentials: true,
        params: {
          limit,
          page,
          business,
          ...query,
        },
      });
      setData(response.data);
      setTotal(response.count);
    } catch (e) {
      Swal.fire({
        title: "Ooops",
        text: e.message,
        icon: "danger",
        confirmButtonColor: "#d33",
        confirmButtonText: "OK",
      });
    } finally {
      setIsLoading(false);
    }
  }, 1000);

  useEffect(() => {
    getReportList();
  }, [page, limit, business]);

  useEffect(() => {
    document.title = `${appConfig.name} - Report List`;
  }, []);

  const handleDownload = async (row) => {
    setDownloadPdf((prev) => [...prev, row._id]);
    try {
      const blob = await pdf(
        <ReactPDF name={row.name} img={row.image?.url} updatedAt={row.updatedAt} firstBusiness={firstBusiness} />,
      ).toBlob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${row.name}_${moment().format("DDMMYY_HHmmss")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      // eslint-disable-next-line no-console
      console.log("An error occurred while downloading the file");
    } finally {
      setDownloadPdf((prev) => prev.filter((d) => d !== row._id));
    }
  };

  const columns = [
    {
      title: "No.",
      // width: "60px",
      render: (_, __, index) => <div>{(page - 1) * limit + index + 1}</div>,
    },
    {
      title: "Report Name",
      render: (row) => row.name,
      maxWidth: "100px",
    },
    {
      title: "Date Generated",
      render: (row) => moment(row.createdAt).format("DD/MM/YYYY HH:mm:ss"),
      // align: "center",
    },
    {
      title: "Generated By",
      render: (row) => row.user?.name,
      maxWidth: "100px",
    },
    {
      title: "User Type",
      render: (row) => userRoleFormate[row.user?.role || "user"],
      maxWidth: "100px",
    },

    {
      title: "Business",
      render: (row) => (row?.business?.name ? `${row?.business?.name}(${row?.business?.id})` : "--"),
      maxWidth: "100px",
    },

    {
      title: "Action",
      width: "280px",
      align: "center",
      render: (row) => (
        <div>
          <Link to={`/report-list/${row._id}`}>
            <button className="btn btn-az-primary  me-1">
              <AiOutlineEye />
            </button>
          </Link>
          {/* EditReport  */}
          <Link to={`/edit-report/${row._id}`}>
            <button className="btn btn-edit me-1">
              <BiEdit />
            </button>
          </Link>
          <button
            className="btn btn-view-gray me-1 d-none d-md-inline"
            title="Copy Report"
            onClick={() => copyHandler(row._id)}
          >
            <BiCopy />
          </button>
          <button className="btn btn-secondary me-2 d-none d-md-inline" onClick={() => handleDownload(row)}>
            {downloadPdf.includes(row._id) ? <Spinner animation="border" role="status" size="sm" /> : <TfiDownload />}
          </button>

          <button className="me-2 btn btn-danger" onClick={() => deleteHandler(row._id)}>
            <MdDeleteOutline />
          </button>
        </div>
      ),
    },
  ];

  const copyHandler = (id) => {
    try {
      Swal.fire({
        title: "Are you sure?",
        text: "This action will duplicate the existing report",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Confirm",
      }).then(async (result) => {
        if (result.isConfirmed) {
          navigate(`/edit-report/${id}?cp=true`);
        }
      });
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  // handler
  const deleteHandler = (id) => {
    try {
      Swal.fire({
        title: "Are you sure?",
        text: "Do you wish to delete this report?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Confirm",
      }).then(async (result) => {
        if (result.isConfirmed) {
          const response = await axios.delete(`${endpoint.remove_customReport}/${id}`, { withCredentials: true });
          if (response.status === 200) {
            Swal.fire("Deleted!", "Report Deleted Successfully!", "success");
            getReportList();
            // getActiveReport();
          }
        }
      });
    } catch (error) {
      flare.report(error);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  return (
    <Spin spinning={isLoading}>
      <div className="px-md-3 mt-3">
        <div className="border p-3">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <label className="az-content-label">Manage Report</label>
              <span className="d-block py-2">Report List</span>
            </div>
            <div className="col-md-4 d-none d-md-flex justify-content-end p-0">
              <Link to="/generate-report">
                <Button icon={<FaPlus />} type="primary">
                  Generate New Report
                </Button>
              </Link>
            </div>
          </div>
          <hr className="mt-0 mb-5" />

          <div>
            <div className="d-flex justify-content-end mb-3">
              <Input
                type="search"
                size="large"
                style={{
                  width: 200,
                }}
                onChange={(e) => {
                  getReportList({ name: e.target.value });
                  setPage(1);
                }}
                placeholder="Search"
                // className="me-2"
                aria-label="Search"
              />
            </div>
            <DataTable
              columns={columns}
              dataSource={data}
              pagination={{
                pageSize: limit,
                current: page,
                total,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                pageSizeOptions: [10, 20, 50],
                responsive: true,
              }}
              onChange={(pagination) =>
                navigate(`/report-list?page=${pagination.current}&limit=${pagination.pageSize}`)
              }
            />
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default CustomReportList;
